import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid2,
  Typography,
  TextField,
  MenuItem,
  Button,
  DialogContentText,
  Divider,
  FormControl,
  FormLabel,
  Select,
  FormHelperText,
  Checkbox
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { useStore } from 'src/store/StoreContext';
import StepContainer from './components/step-container';
import { CalendarMonthOutlined } from '@mui/icons-material';
import DailogPopup from 'src/layouts/components/dailog';
import toast from 'react-hot-toast';
import { Filter } from "bad-words";
import {
  checkEmailExists,
  checkPatientExists,
  generateOTP,
  validateConsultantAndGetMediRecordId,
  validateOtp
} from 'src/services/registeration/registerationService';
import { generateotprequest } from 'src/models/generateotp';
import { useNavigate } from 'react-router';

const filter = new Filter();


interface PersonDetails {
  title: string;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
  email: string;
  phoneNumber: string;
  MediRecordID?: string | null;
}

interface Address {
  address1: string;
  address2?: string;
  suburb: string;
  state: string;
  postalCode: string;
  medicare: string;
  irn: string;
  medicareExpiry: string;
}

export class RegisterationFormValues {
  myDetails: PersonDetails;
  address: Address;
  isCreateProfile?: boolean;

  constructor() {
    this.myDetails = {
      title: '',
      firstName: '',
      lastName: '',
      gender: '',
      dob: formatDateForAPI(new Date()) || '',
      email: '',
      phoneNumber: '',
      MediRecordID: '',
    };
    this.address = {
      address1: '',
      address2: '',
      suburb: '',
      state: '',
      postalCode: '',
      medicare: '',
      irn: '',
      medicareExpiry: null as any,
    };
    this.isCreateProfile = false;
  }
}

const myDetailsSchema = {
  title: yup.string().required('Title is required'),
  firstName: yup
    .string()
    .required('First name is required')
    .matches(/^[A-Za-z\s]+$/, 'Only alphabets are allowed')
    .test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
  lastName: yup
    .string()
    .required('Last name is required')
    .matches(/^[A-Za-z\s]+$/, 'Only alphabets are allowed')
    .test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
  gender: yup.string().required('Gender is required'),
  email: yup
    .string()
    .email('Invalid email')
    .required('Email is required')
    .test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
  phoneNumber: yup
    .string()
    .required('Mobile number is required')
    .matches(/^(?:\+61\d{9}|0\d{9})$/, 'Mobile number must start with +61 or 0 followed by 9 digits')
    .test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
  dob: yup
    .string()
    .required('Date of birth is required')
    .test('age-check', 'You must be at least 18 years old', function (value) {
      if (!value) return false;
      const dob = new Date(value);
      const minAge = new Date();
      minAge.setFullYear(minAge.getFullYear() - 18);
      return dob <= minAge;
    }),
  MediRecordID: yup.string().nullable(),
};

const States = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' },
];

export const formatDateForAPI = (reqDate: Date | string | null): string => {
  if (!reqDate) return '';
  let date = new Date(reqDate);
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return reqDate as string;
};

const RequiredLabel = (label: string) => (
  <span>
    {label} <span style={{ color: 'red' }}>*</span>
  </span>
);

const titleOptions = [
  { label: 'Mr', value: 'mr', code: '315890000' },
  { label: 'Mrs', value: 'mrs', code: '315890001' },
  { label: 'Miss', value: 'miss', code: '315890003' },
  { label: 'Ms', value: 'ms', code: '315890002' },
  { label: 'Mx', value: 'mx', code: '315890012' },
  { label: 'Dr', value: 'dr', code: '315890004' },
  { label: 'Prof', value: 'prof', code: '315890005' },
  { label: 'Sir', value: 'sir', code: '315890006' },
  { label: 'Lady', value: 'lady', code: '315890007' },
  { label: 'Br', value: 'br', code: '315890008' },
  { label: 'Fr', value: 'fr', code: '315890009' },
  { label: 'Sr', value: 'sr', code: '315890010' },
  { label: 'Master', value: 'master', code: '315890011' },
];

const addressSchema = {
  address1: yup
    .string()
    .required('Address 1 is required')
    .test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
  address2: yup.string().test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
  suburb: yup
    .string()
    .required('Suburb is required')
    .test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
  state: yup
    .string()
    .oneOf(States.map(s => s.value), 'Select a valid state')
    .required('State is required')
    .test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
  postalCode: yup.string().required('Postal code is required').min(4, 'Postal code should be 4 digits').max(4, 'Postal code should be 4 digits'),
  medicare: yup.string().required('Medicare Number is required').min(10, 'Medicare Number should be 10 digits').max(10, 'Medicare Number should be 10 digits'),
  irn: yup.string().required("IRN is required"),
  medicareExpiry: yup
    .string()
    .required('Medicare expiry date is required')
    .test('future-date', 'Expiry date must be in the future', function (value) {
      if (!value) return false;
      const expiryDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return expiryDate > today;
    }),
};

const schema = yup.object().shape({
  myDetails: yup.object().shape(myDetailsSchema),
  address: yup.object().shape(addressSchema),
  isCreateProfile: yup.bool(),
});

interface FormFieldProps {
  label: React.ReactNode;
  name: string;
  control: any;
  props?: any;
  component?: React.ComponentType<any>;

}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  control,
  props,
  component: Component = TextField,

}) => (
  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <Component
            size='small'
            isLabel={true}
            margin='dense'
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}

            {...field}
            {...props}
            label={label}
            fullWidth
            error={!!error}
            helperText={error?.message}
          />
        </>
      )}
    />
  </Grid2>
);

interface FieldProps {
  control: any;
  prefix: string;
  showRelationshipField?: boolean;
}

const PersonDetailsFields: React.FC<FieldProps & { tabStartIndex?: number }> = ({
  control,
  prefix,
  showRelationshipField,
  tabStartIndex = 1,
}) => (
  <>
    {/* Title */}
    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
      <Controller
        name={`${prefix}.title`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            select
            size="small"
            margin="dense"

            slotProps={{ inputLabel: { shrink: true } }}
            {...field}
            fullWidth
            error={!!error}
            helperText={error?.message}
            label={RequiredLabel("Title")}
          >
            {titleOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Grid2>

    <FormField
      label={RequiredLabel("First Name")}
      name={`${prefix}.firstName`}
      control={control}

    />
    <FormField
      label={RequiredLabel("Last Name")}
      name={`${prefix}.lastName`}
      control={control}

    />

    {/* Gender */}
    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
      <Controller
        name={`${prefix}.gender`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            select
            size="small"
            margin="dense"

            slotProps={{ inputLabel: { shrink: true } }}
            {...field}
            fullWidth
            error={!!error}
            helperText={error?.message}
            label={RequiredLabel("Gender")}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        )}
      />
    </Grid2>

    {/* DOB */}
    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
      <Controller
        name={`${prefix}.dob`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <MobileDatePicker
            format="dd/MM/yyyy"
            disableHighlightToday
            disableFuture
            {...field}
            label={RequiredLabel("Date of Birth")}
            sx={{ mt: 1 }}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                error: !!error,
                helperText: error?.message,

                slotProps: {
                  inputLabel: { shrink: true },
                },
              },
            }}
          />
        )}
      />
    </Grid2>

    {/* Email */}

    <FormField
      label={RequiredLabel("Email")}
      name={`${prefix}.email`}
      control={control}

    />

    {/* Phone */}
    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
      <Controller
        name={`${prefix}.phoneNumber`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            label={RequiredLabel("Phone Number")}
            size="small"
            margin="dense"

            slotProps={{ inputLabel: { shrink: true } }}
            {...field}
            fullWidth
            value={
              field.value?.startsWith('+61')
                ? field.value.replace(/^\+61(\d{3})(\d{3})(\d{3})$/, '+61 $1 $2 $3')
                : field.value?.startsWith('0')
                  ? field.value.replace(/^0(\d{3})(\d{3})(\d{3})$/, '0$1 $2 $3')
                  : field.value || ''
            }
            onChange={(e) => {
              let input = e.target.value.replace(/[^\d+]/g, '');

              if (input.startsWith('0')) {
                input = input.slice(0, 10);
              } else if (input.startsWith('+61')) {
                input = input.slice(0, 12);
              }

              field.onChange(input);
            }}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </Grid2>
  </>
);

const AddressFields: React.FC<FieldProps & { tabStartIndex?: number }> = ({
  control,
  prefix,
  tabStartIndex = 8,
}) => (
  <>
    <FormField label={RequiredLabel("Address 1")} name={`${prefix}.address1`} control={control} />
    <FormField label="Address 2" name={`${prefix}.address2`} control={control} />
    <FormField label={RequiredLabel("Suburb")} name={`${prefix}.suburb`} control={control} />

    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
      <Controller
        name={`${prefix}.state`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            select
            size="small"
            label={RequiredLabel("State")}
            fullWidth
            sx={{ mt: 1 }}
            // tabIndex={tabStartIndex + 3}  
            error={!!error}
            helperText={error?.message}
            slotProps={{ inputLabel: { shrink: true } }}
          >
            {States.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Grid2>

    <FormField label={RequiredLabel("Postal Code")} name={`${prefix}.postalCode`} control={control} />

    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
      <Controller
        name={`${prefix}.medicare`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            label={RequiredLabel("Medicare")}
            size="small"
            margin="dense"
            slotProps={{ inputLabel: { shrink: true } }}
            {...field}
            fullWidth
            value={field.value?.replace(/\D/g, '').replace(/(\d{4})(\d{0,5})(\d{0,1})/, '$1 $2 $3').trim() || ''}
            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </Grid2>

    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
      <Controller
        name={`${prefix}.irn`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            select
            size="small"
            margin="dense"
            label={RequiredLabel("IRN Number")}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
            error={!!error}
            helperText={error?.message}
            {...field}
          >
            <MenuItem value="">
              <em>Select IRN Number</em>
            </MenuItem>
            {[...Array(9)].map((_, i) => (
              <MenuItem key={i} value={(i + 1).toString()}>
                {i + 1}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Grid2>

    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
      <Controller
        name={`${prefix}.medicareExpiry`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <MobileDatePicker
            views={['year', 'month']}
            label={RequiredLabel("Medicare Expiry Date")}
            format="yyyy/MM"
            sx={{ mt: 1 }}
            disablePast
            value={field.value}
            onChange={(date) => field.onChange(date)}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: !!error,
                helperText: error?.message,
                InputLabelProps: { shrink: true },
              },
            }}
          />
        )}
      />
    </Grid2>
  </>
);

const PatientRegisteration = observer(() => {
  const service = useStore()?.service;
  const {
    control,
    setValue,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<RegisterationFormValues>({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: service?.personalDetails
      ? {
        ...JSON.parse(JSON.stringify(service.personalDetails)),
        isCreateProfile: true
      }
      : { isCreateProfile: true }
  });

  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpUuid, setOtpUuid] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [details] = useState(service?.personalDetails);
  const [otpTimer, setOtpTimer] = useState<number>(90);
  const [otpExpired, setOtpExpired] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [emailExistsPopup, setEmailExistsPopup] = useState(false);
  const [showAccountExistsDialog, setShowAccountExistsDialog] = useState(false);
  const navigate = useNavigate();

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const onSubmit = async (data: RegisterationFormValues) => {
    try {
      const emailExists = await checkEmailExists(data.myDetails.email);
      if (emailExists?.data === true) {
        setEmailExistsPopup(true);
        return;
      }

      const patientExists = await checkPatientExists(
        data.myDetails.email,
        data.myDetails.firstName,
        data.myDetails.lastName,
        formatDateForAPI(data.myDetails.dob) || ''
      );

      // if (patientExists?.data === true) {
      //   toast.success("Your details will be stored under your existing profile.");
      // }

      const phoneChanged = details?.myDetails.phoneNumber !== data.myDetails.phoneNumber;

      if (phoneChanged) {
        const generateOtpRequest = new generateotprequest(
          data.myDetails.email,
          data.myDetails.phoneNumber,
          data.myDetails.firstName,
          data.myDetails.lastName,
          formatDateForAPI(data.myDetails.dob) || ''
        );

        const otpResponse = await generateOTP(generateOtpRequest);

        if (otpResponse.ok) {
          setOtpUuid(otpResponse.data?.otpUuid || null);
          setOtpDialogOpen(true);

          setOtp('');
          setOtpExpired(false);
          setCanResend(false);
          setOtpTimer(90);

          if (timerRef.current) {
            clearInterval(timerRef.current);
          }

          timerRef.current = setInterval(() => {
            setOtpTimer((prev) => {
              if (prev <= 1) {
                clearInterval(timerRef.current!);
                setCanResend(true);
                setOtpExpired(true);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else {
          toast.error(otpResponse.message || 'Failed to generate OTP');
        }
      } else {
        await service?.updatePersonalDetails(data);
        toast.success("Details updated successfully");
      }
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error('Something went wrong');
    }
  };

  const handleOtpSubmit = async () => {
    try {
      setLoading(true);

      if (!otp || !otpUuid) {
        toast.error('OTP or OTP UUID is missing');
        return;
      }

      if (otpExpired) {
        toast.error('OTP has expired. Please request a new one.');
        return;
      }

      const isOtpValidResponse = await validateOtp({ otp, otpUuid });

      if (!isOtpValidResponse.ok || !isOtpValidResponse.data) {
        toast.error(isOtpValidResponse.message || 'Invalid OTP');
        return;
      }

      const formData = getValues();
      const selectedTitle = titleOptions.find(
        option => option.value === (formData.myDetails as { title?: string })?.title
      );
      const titleCode = selectedTitle?.code ?? 'other';

      const newPayload = {
        personalDetails: {
          myDetails: {
            firstName: formData.myDetails.firstName,
            lastName: formData.myDetails.lastName,
            gender: formData.myDetails.gender,
            dob: formatDateForAPI(formData.myDetails.dob),
            email: formData.myDetails.email,
            phoneNumber: formData.myDetails.phoneNumber,
            title: titleCode,
            otp,
            otpUuid,
            MediRecordID: formData.myDetails.MediRecordID,
          },
          address: {
            address1: formData.address.address1,
            address2: formData.address.address2,
            suburb: formData.address.suburb,
            state: formData.address.state,
            postalCode: formData.address.postalCode,
            medicare: formData.address.medicare,
            irn: formData.address.irn,
            medicareExpiry: formatDateForAPI(formData.address.medicareExpiry),
          },
          isCreateProfile: formData.isCreateProfile,
        },
      };
      const response = await validateConsultantAndGetMediRecordId(newPayload);

      if (!response.ok) {
        toast.error(response.message || 'Validation failed');
        setOtpDialogOpen(false);
        return;
      }

      newPayload.personalDetails.myDetails.MediRecordID = response.data;

      await service?.updatePersonalDetails(newPayload.personalDetails);
      toast.success('OTP verified');
      setOtpDialogOpen(false);
      if (timerRef.current) clearInterval(timerRef.current);
    } catch (error) {
      console.error('OTP verification failed:', error);
      toast.error('OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
      setOtp('');
    }
  };

  const handleResendOtp = async () => {
    const data = getValues();
    const generateOtpRequest = new generateotprequest(
      data.myDetails.email,
      data.myDetails.phoneNumber,
      data.myDetails.firstName,
      data.myDetails.lastName,
      formatDateForAPI(data.myDetails.dob) || ''
    );

    const otpResponse = await generateOTP(generateOtpRequest);

    if (otpResponse.ok) {
      toast.success('OTP resent successfully');
      setOtpUuid(otpResponse.data?.otpUuid || null);
      setOtp('');
      setOtpExpired(false);
      setCanResend(false);
      setOtpTimer(90);

      if (timerRef.current) clearInterval(timerRef.current);

      timerRef.current = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            setCanResend(true);
            setOtpExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      toast.error(otpResponse.message || 'Failed to resend OTP');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <StepContainer
        icon='fluent-color:clipboard-text-edit-20'
        title='Patient Registration'
        cancelButtonProps={{ disabled: service?.contentLoading }}
        confirmButtonProps={{ type: 'submit', disabled: service?.contentLoading, loading: service?.contentLoading }}
      >
        <Typography variant='h6'>Basic Information</Typography>
        <Grid2 container spacing={2}>
          <PersonDetailsFields control={control} prefix="myDetails" />
          <AddressFields control={control} prefix="address" />

          <Grid2 size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Controller
                  name="isCreateProfile"
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} checked={field.value} />
                  )}
                />
              }
              label="I want to create a profile with this information"
              labelPlacement="end"
              slotProps={{
                typography: {
                  variant: 'body2',
                }
              }}
            />
            <Typography variant="body2" color="textSecondary">
              Note : Fields marked with an asterisk (*) are required.
            </Typography>
          </Grid2>
        </Grid2>

        <DailogPopup
          open={otpDialogOpen}
          onCancel={() => setOtpDialogOpen(false)}
          onSubmit={handleOtpSubmit}
          loading={loading}
          title={"Enter OTP"}
          disableSubmit={otpTimer === 0}
          resendButton={
            otpTimer === 0 ? (
              <Button
                onClick={handleResendOtp}
                variant="contained"
                color="primary"

              >
                Resend OTP
              </Button>
            ) : null
          }
        >
          <DialogContentText>
            Please enter the 4-digit OTP sent to your registered phone.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="OTP"
            type="text"
            fullWidth

            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            disabled={otpTimer === 0}
          />
          <Typography
            variant="caption"
            color={otpTimer === 0 ? 'error' : 'textSecondary'}
            sx={{ mt: 1 }}
          >
            {otpTimer > 0
              ? `OTP expires in ${otpTimer} second${otpTimer === 1 ? '' : 's'}`
              : 'OTP expired. Click "Resend OTP" to get a new one.'}
          </Typography>
        </DailogPopup>

        <DailogPopup
          open={emailExistsPopup}
          onSubmit={() => {
            setEmailExistsPopup(false);
            navigate('/login');
          }}
          title="Account Already Exists"
          submitText="OK"
          hideCancelButton
        >
          <DialogContentText>
            An account with this email already exists. Please log in instead.
          </DialogContentText>
        </DailogPopup>
      </StepContainer>
    </form>
  );
});

export default PatientRegisteration;
